import { useState } from 'react';
import type { PublicUser } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { BigButton } from '../BigButton.js';

export interface HandoverDialogProps {
  user: PublicUser;
  onClose: () => void;
  onHandedOver: (user: PublicUser) => void;
}

/**
 * "Hand over to new person" (admin → Users). Distinct from a plain rename:
 * it resets the login AND the password in one step, for the day someone
 * leaves and a replacement takes over their account. The account's short
 * code (and the uuid backing it) never change, so this dialog leads with
 * that fact and an explicit warning that past records keep showing the
 * previous person's name - the whole point of a snapshot (spec §4).
 */
export function HandoverDialog({ user, onClose, onHandedOver }: HandoverDialogProps) {
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const canSubmit =
    displayName.trim().length > 0 && username.trim().length > 0 && password.length >= 8 && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const updated = await api.handoverUser(user.id, {
        displayName: displayName.trim(),
        username: username.trim(),
        password,
        note: note.trim(),
      });
      onHandedOver(updated);
    } catch (err) {
      setApiError(err instanceof ApiError ? err.message : 'Could not complete the handover');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 sm:p-6"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="handover-title"
        className="w-full max-w-sm bg-surface rounded-t-2xl sm:rounded-xl shadow-xl p-4 sm:p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 id="handover-title" className="font-heading font-semibold text-lg text-ink">
            Hand over to new person
          </h2>
          <p className="font-body text-sm text-muted mt-1">
            Currently {user.displayName} &middot; <span className="font-semibold">{user.shortCode}</span>
          </p>
        </div>

        <p role="alert" className="font-body text-sm text-ink bg-aqua rounded-lg px-3 py-2">
          Past sales and stock records keep showing <span className="font-semibold">{user.displayName}</span> and the
          code <span className="font-semibold">{user.shortCode}</span> - only sales made from now on will show the
          new name. The code itself never changes.
        </p>

        <div>
          <label htmlFor="handover-display-name" className="block font-body font-semibold text-base text-teal-dark mb-1">
            New person&apos;s display name
          </label>
          <input
            id="handover-display-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={submitting}
            className="w-full min-h-12 rounded-lg border-2 border-border px-4 text-base font-body"
          />
        </div>

        <div>
          <label htmlFor="handover-username" className="block font-body font-semibold text-base text-teal-dark mb-1">
            New login username
          </label>
          <input
            id="handover-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            className="w-full min-h-12 rounded-lg border-2 border-border px-4 text-base font-body"
          />
        </div>

        <div>
          <label htmlFor="handover-password" className="block font-body font-semibold text-base text-teal-dark mb-1">
            New password (min 8 characters)
          </label>
          <div className="flex gap-2">
            <input
              id="handover-password"
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              className="input flex-1"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              className="min-h-12 px-3 rounded-lg font-body font-semibold text-base text-teal-dark border-2 border-border"
            >
              {passwordVisible ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="handover-note" className="block font-body font-semibold text-base text-teal-dark mb-1">
            Note (optional)
          </label>
          <input
            id="handover-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            disabled={submitting}
            className="w-full min-h-12 rounded-lg border-2 border-border px-4 text-base font-body"
          />
        </div>

        {apiError && (
          <p role="alert" className="font-body text-sm text-danger">
            {apiError}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <BigButton variant="outline" onClick={onClose}>
            Cancel
          </BigButton>
          <BigButton disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {submitting ? 'Handing over…' : 'Hand over'}
          </BigButton>
        </div>
      </div>
    </div>
  );
}
